import { subject } from '@casl/ability';
import { Stack } from '@mantine-8/core';
import { type FC } from 'react';
import { useParams } from 'react-router';
import { useUnmount } from 'react-use';
import ErrorState from '../components/common/ErrorState';
import Page from '../components/common/Page/Page';
import ForbiddenPanel from '../components/ForbiddenPanel';
import { HomepageContentPanel } from '../components/Home/HomepageContentPanel';
import LandingPanel from '../components/Home/LandingPanel';
import OnboardingPanel from '../components/Home/OnboardingPanel/index';
import PageSpinner from '../components/PageSpinner';
import PinnedAndFavoritesSection from '../components/PinnedAndFavoritesSection';
import AiSearchBox from '../ee/components/Home/AiSearchBox';
import { useAiAgentButtonVisibility } from '../ee/features/aiCopilot/hooks/useAiAgentsButtonVisibility';
import { useFavorites } from '../hooks/favorites/useFavorites';
import { usePinnedItems } from '../hooks/pinning/usePinnedItems';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import {
    useMostPopularAndRecentlyUpdated,
    useProject,
} from '../hooks/useProject';
import useApp from '../providers/App/useApp';
import { FavoritesProvider } from '../providers/Favorites/FavoritesProvider';
import { PinnedItemsProvider } from '../providers/PinnedItems/PinnedItemsProvider';

const Home: FC = () => {
    const params = useParams<{ projectUuid: string }>();
    const selectedProjectUuid = params.projectUuid;
    const project = useProject(selectedProjectUuid);
    const onboarding = useOnboardingStatus();
    const pinnedItems = usePinnedItems(
        selectedProjectUuid,
        project.data?.pinnedListUuid,
    );
    const favorites = useFavorites(selectedProjectUuid);
    const {
        data: mostPopularAndRecentlyUpdated,
        isInitialLoading: isMostPopularAndRecentlyUpdatedLoading,
    } = useMostPopularAndRecentlyUpdated(selectedProjectUuid);

    const { user } = useApp();
    const isAiAgentsEnabled = useAiAgentButtonVisibility();

    const isLoading =
        onboarding.isInitialLoading ||
        project.isInitialLoading ||
        isMostPopularAndRecentlyUpdatedLoading ||
        pinnedItems.isInitialLoading ||
        favorites.isInitialLoading;

    const error = onboarding.error || project.error;

    useUnmount(() => onboarding.remove());

    if (isLoading) {
        return <PageSpinner />;
    }

    if (error) {
        return <ErrorState error={error.error} />;
    }

    if (!project.data || !onboarding.data) {
        return <ErrorState />;
    }

    if (user.data?.ability?.cannot('view', subject('Project', project.data))) {
        return <ForbiddenPanel />;
    }

    return (
        <Page withFixedContent withPaddedContent withFooter>
            <Stack gap="xl">
                {!onboarding.data.ranQuery ? (
                    <OnboardingPanel
                        projectUuid={project.data.projectUuid}
                        userName={user.data?.firstName}
                    />
                ) : (
                    <FavoritesProvider projectUuid={project.data.projectUuid}>
                        <LandingPanel
                            userName={user.data?.firstName}
                            projectUuid={project.data.projectUuid}
                        />
                        {isAiAgentsEnabled && (
                            <AiSearchBox
                                projectUuid={project.data.projectUuid}
                            />
                        )}
                        <PinnedItemsProvider
                            organizationUuid={project.data.organizationUuid}
                            projectUuid={project.data.projectUuid}
                            pinnedListUuid={project.data.pinnedListUuid || ''}
                            allowDelete={false}
                        >
                            <PinnedAndFavoritesSection
                                pinnedItems={pinnedItems.data ?? []}
                                favoriteItems={favorites.data ?? []}
                                pinnedIsEnabled={Boolean(
                                    mostPopularAndRecentlyUpdated?.mostPopular
                                        .length ||
                                    mostPopularAndRecentlyUpdated
                                        ?.recentlyUpdated.length,
                                )}
                            />
                        </PinnedItemsProvider>
                        <HomepageContentPanel
                            data={mostPopularAndRecentlyUpdated}
                            projectUuid={project.data.projectUuid}
                        />
                    </FavoritesProvider>
                )}
            </Stack>
        </Page>
    );
};

export default Home;
